// Per-segment outer-perimeter geometry for the carrier bold-outline convention.
//
// TrakGene-specific convention (see PLAN-carrier-status-pedigree.md): a carrier
// disease segment is drawn with a BOLD line on its outer perimeter in the
// disease colour, replacing the standard thin outline on that segment. These
// helpers produce the perimeter geometry for circle (female) and square (male)
// nodes so each segment's perimeter can be stroked independently.
//
// Angle convention matches d3.pie: 0 = 12 o'clock, increasing clockwise. A
// point at angle θ / radius ρ in SVG coords (y down) is (ρ·sinθ, −ρ·cosθ),
// identical to d3.arc's own centroid maths.

const TAU = Math.PI * 2;
const EPS = 1e-9;

// Square corners in pie-angle terms: up-right=45°, down-right=135°,
// down-left=225°, up-left=315°.
const SQUARE_CORNER_ANGLES = [
  Math.PI / 4,
  (3 * Math.PI) / 4,
  (5 * Math.PI) / 4,
  (7 * Math.PI) / 4
];

const round = (n) => Math.round(n * 1e4) / 1e4;

export function pointOnCircle(angle, r) {
  return { x: round(r * Math.sin(angle)), y: round(-r * Math.cos(angle)) };
}

// Intersection of the ray at `angle` with the square [-h,h]² boundary.
export function pointOnSquare(angle, h) {
  const sx = Math.sin(angle);
  const cy = -Math.cos(angle);
  const m = Math.max(Math.abs(sx), Math.abs(cy));
  if (m < EPS) return { x: 0, y: 0 };
  const t = h / m;
  return { x: round(t * sx), y: round(t * cy) };
}

export function getRadialDividerEnd(angle, shape) {
  return shape.type === 'square'
    ? pointOnSquare(angle, shape.h)
    : pointOnCircle(angle, shape.r);
}

// Outer arc of one circle segment as an SVG path string.
export function getCircleSegmentArc({ startAngle, endAngle }, r) {
  const span = endAngle - startAngle;
  if (span >= TAU - EPS) {
    // Single full-circle segment → two semicircle arcs (an A command can't
    // draw a 360° arc because start and end points coincide).
    const top = pointOnCircle(0, r);
    const bottom = pointOnCircle(Math.PI, r);
    return (
      `M ${top.x} ${top.y} ` +
      `A ${round(r)} ${round(r)} 0 1 1 ${bottom.x} ${bottom.y} ` +
      `A ${round(r)} ${round(r)} 0 1 1 ${top.x} ${top.y}`
    );
  }
  const p1 = pointOnCircle(startAngle, r);
  const p2 = pointOnCircle(endAngle, r);
  const largeArc = span > Math.PI ? 1 : 0;
  const sweep = 1; // d3 angles increase clockwise → SVG sweep-flag 1
  return `M ${p1.x} ${p1.y} A ${round(r)} ${round(r)} 0 ${largeArc} ${sweep} ${
    p2.x
  } ${p2.y}`;
}

// Outer perimeter of one square segment as an ordered list of points:
// [ start-edge point, ...any corners traversed (in angular order)..., end-edge
// point ]. Render as an SVG <polyline>/path. Assumes angles in [0, 2π].
export function getSquareSegmentPolyline({ startAngle, endAngle }, h) {
  const points = [pointOnSquare(startAngle, h)];
  for (const corner of SQUARE_CORNER_ANGLES) {
    if (corner > startAngle + EPS && corner < endAngle - EPS) {
      points.push(pointOnSquare(corner, h));
    }
  }
  points.push(pointOnSquare(endAngle, h));
  return points;
}

// Convenience: a segment's perimeter for either shape. Circles return
// { type:'arc', d } and squares { type:'polyline', points }.
export function getSegmentPerimeter(segment, shape) {
  return shape.type === 'square'
    ? { type: 'polyline', points: getSquareSegmentPolyline(segment, shape.h) }
    : { type: 'arc', d: getCircleSegmentArc(segment, shape.r) };
}

// Vertical carrier line (Bennett 2022 single-disease convention): a line centred
// horizontally on the node, running from above the top edge down to the bottom
// edge. `shape` is { type:'circle', r } or { type:'square', h } (half-extent from
// centre, so node height = 2·half). `protrusionRatio` is the portion ABOVE the top
// edge as a fraction of node HEIGHT (~0.25–0.30). Returns top/bottom endpoints in
// node-local coords (y down, origin at node centre).
export function getVerticalLineEnds(shape, protrusionRatio = 0.275) {
  const half = shape.type === 'square' ? shape.h : shape.r;
  const protrusion = protrusionRatio * 2 * half;
  return {
    top: { x: 0, y: round(-(half + protrusion)) },
    bottom: { x: 0, y: round(half) }
  };
}
